/**
 * `P0-UIK-009`: phone normalisation, section 06, 6.5.4, and check `C13`.
 *
 * **Danger sentence.** The normalised E.164 is the DEDUPLICATION KEY (6.5.4):
 * `+223 76 44 55 66`, `0022376445566` and `76445566` are one client. A
 * normaliser that disagrees with itself across two call sites does not produce
 * a formatting inconsistency , it produces two clients, two dossiers and two
 * credit histories for one person, and nobody notices until a bank asks why
 * Traoré appears twice.
 */

export const MALI_PREFIX = "+223";

/** 6.5.4: 8 significant digits, first in {5,6,7,8,9} for a mobile. */
const MALI_MOBILE_FIRST_DIGITS = new Set(["5", "6", "7", "8", "9"]);

export interface PhoneNumber {
  /** E.164, no spaces. What is stored and what deduplicates. */
  readonly e164: string;
  /** True when it is a Malian mobile, which is the only client login identifier. */
  readonly mobileMalien: boolean;
}

export class PhoneInvalid extends Error {
  /** 4.5.5's code, so a caller maps it without inspecting the message. */
  readonly code = "invalid_request_body";
  constructor(message: string) {
    super(message);
    this.name = "PhoneInvalid";
  }
}

/**
 * 6.5.4's normalisation, in canon's own order: strip spaces, dots, dashes and
 * parentheses; strip a leading `00` and replace it with `+`; if the result has
 * 8 digits and no prefix, it is Malian.
 *
 * Foreign numbers are ACCEPTED and stored in E.164 when a valid country prefix
 * is given , canon's reason is that a Malian diaspora buyer exists, and
 * refusing them would be refusing a real customer to keep a regex simple.
 */
export function normalisePhone(input: string): PhoneNumber {
  const stripped = input.replace(/[\s.\-()]/gu, "");
  if (stripped === "") throw new PhoneInvalid("Un numéro de téléphone est requis.");

  const withPlus = stripped.startsWith("00") ? `+${stripped.slice(2)}` : stripped;

  if (withPlus.startsWith("+")) {
    if (!/^\+\d{8,15}$/u.test(withPlus)) {
      throw new PhoneInvalid(`Numéro de téléphone invalide : ${input}`);
    }
    return { e164: withPlus, mobileMalien: isMalianMobile(withPlus) };
  }

  if (!/^\d+$/u.test(withPlus)) {
    throw new PhoneInvalid(`Numéro de téléphone invalide : ${input}`);
  }
  // Eight digits and no prefix is Malian, which is the default country and the
  // only case where a prefix may be assumed. Seven digits is not a short
  // Malian number, it is a typo, and accepting it would mint a second client.
  if (withPlus.length !== 8) {
    throw new PhoneInvalid(`Numéro de téléphone invalide : ${input}`);
  }
  const e164 = `${MALI_PREFIX}${withPlus}`;
  return { e164, mobileMalien: isMalianMobile(e164) };
}

/** A Malian number whose 8 significant digits start with 5, 6, 7, 8 or 9. */
export function isMalianMobile(e164: string): boolean {
  if (!e164.startsWith(MALI_PREFIX)) return false;
  const significant = e164.slice(MALI_PREFIX.length);
  const first = significant.at(0);
  return significant.length === 8 && first !== undefined && MALI_MOBILE_FIRST_DIGITS.has(first);
}

/**
 * 6.5.4's display form: `+223 76 44 55 66` , prefix, then pairs, separated by
 * NORMAL spaces. Not the narrow no-break space money uses: a phone number is
 * read aloud in pairs and is allowed to wrap.
 */
export function formatPhone(e164: string): string {
  // MALI ONLY, deliberately. 6.5.4 specifies this display for the Malian
  // number and says nothing about a foreign one, and the grouping conventions
  // differ by country , `+33 6 12 34 56 78` is not pairs-after-the-prefix.
  // The first cut used `^(\+\d{1,4})(\d+)$`, whose greedy prefix ate a digit
  // and rendered `+22376445566` as `+2237 64 45 56 6`: a phone number a reader
  // would dial wrong. A foreign number is returned in E.164 rather than
  // grouped by a rule nobody wrote down.
  if (!e164.startsWith(MALI_PREFIX)) return e164;
  const significant = e164.slice(MALI_PREFIX.length);
  const pairs = significant.match(/\d{1,2}/gu) ?? [];
  return [MALI_PREFIX, ...pairs].join(" ");
}

/**
 * The WhatsApp adapter's form: no `+`. 6.5.4 is explicit that the ADAPTER
 * strips it and *"no other code does"*, so this lives here, named for its one
 * caller, rather than as a `.replace()` somewhere in a message handler.
 */
export const whatsappNumber = (e164: string): string => e164.replace(/^\+/u, "");
